import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { applyTheme, readStoredTheme, storeTheme, type ThemeMode } from './theme';

type ThemeContextValue = {
  theme: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
  setThemeLock: (mode: ThemeMode | null) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => readStoredTheme());
  const [themeLock, setThemeLock] = useState<ThemeMode | null>(null);
  const activeTheme = themeLock ?? theme;

  useEffect(() => {
    applyTheme(activeTheme);
    if (themeLock == null) {
      storeTheme(theme);
    }
  }, [activeTheme, theme, themeLock]);

  const toggleTheme = useCallback(() => {
    if (themeLock != null) {
      return;
    }
    setThemeState((current) => (current === 'dark' ? 'light' : 'dark'));
  }, [themeLock]);

  const setTheme = useCallback(
    (mode: ThemeMode) => {
      if (themeLock != null) {
        return;
      }
      setThemeState(mode);
    },
    [themeLock]
  );

  const value = useMemo(
    () => ({ theme: activeTheme, toggleTheme, setTheme, setThemeLock }),
    [activeTheme, toggleTheme, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/** Force a theme while mounted. Does not write the saved choice. */
export function useLockTheme(mode: ThemeMode | null): void {
  const { setThemeLock } = useTheme();

  useLayoutEffect(() => {
    setThemeLock(mode);
    return () => setThemeLock(null);
  }, [mode, setThemeLock]);
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return value;
}
